"use client";

import React, { useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Loader2,
  Mail,
} from "lucide-react";

type FormState = "idle" | "submitting" | "success" | "error";

export default function RequestAccessForm() {
  const [formState, setFormState] = useState<FormState>("idle");
  const [showOptional, setShowOptional] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    work_email: "",
    first_name: "",
    company_name: "",
    last_name: "",
    role: "",
    company_website: "",
    monthly_outreach_volume: "",
    primary_use_case: "",
    website_url: "",
  });

  const labelClass =
    "mb-2 block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-slate-300";
  const inputClass =
    "w-full rounded-2xl border border-white/6 bg-black/24 px-4 py-3 text-sm text-white placeholder:text-slate-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] transition-[border-color,background-color,box-shadow] focus:outline-none focus:border-accent-blue/70 focus:bg-black/30 focus:ring-4 focus:ring-accent-blue/10";
  const selectClass = `${inputClass} appearance-none`;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "work_email") {
      setEmailError("");
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(formData.work_email)) return;

    if (formData.website_url) {
      setFormState("success");
      return;
    }

    setFormState("submitting");
    setErrorMessage("");

    try {
      const cleanPayload = Object.fromEntries(
        Object.entries(formData).filter(
          ([key, value]) => key !== "website_url" && value !== ""
        )
      );

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/access-requests`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(cleanPayload),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || "Something went wrong. Please try again.");
      }

      setFormState("success");
    } catch (err: unknown) {
      setFormState("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong. Please try again."
      );
    }
  };

  if (formState === "success") {
    return (
      <section id="request-access" className="section-light relative overflow-hidden py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="request-access-shell relative overflow-hidden rounded-[40px] px-6 py-12 sm:px-10 sm:py-14">
            <div className="request-access-animated-bg" aria-hidden="true" />
            <div className="request-access-animated-ink" aria-hidden="true" />

            <div className="relative z-10 mx-auto max-w-3xl text-center">
              <div className="request-access-form-card rounded-[32px] p-8 text-center sm:p-10">
                <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-[20px] border border-accent-blue/25 bg-accent-blue/10 text-accent-blue">
                  <CheckCircle2 className="h-7 w-7" />
                </div>

                <div className="eyebrow-pill mb-4 inline-flex items-center gap-2 rounded-full border border-accent-blue/20 bg-accent-blue/8 text-[11px] font-semibold uppercase tracking-[0.16em] text-accent-blue">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-blue" />
                  Request received
                </div>

                <h2 className="text-3xl font-bold leading-tight text-white sm:text-4xl">
                  Your access request is in review.
                </h2>

                <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-300">
                  We will review your details and send the next steps by email. Most qualified
                  requests hear back within 48 hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="request-access" className="section-light relative overflow-hidden py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="request-access-shell relative overflow-hidden rounded-[40px] px-6 py-10 sm:px-10 sm:py-12">
          <div className="request-access-animated-bg" aria-hidden="true" />
          <div className="request-access-animated-ink" aria-hidden="true" />

          <div className="relative z-10 grid gap-10 lg:grid-cols-[0.9fr_1.05fr] lg:items-center">
            <div className="mx-auto max-w-lg text-center lg:mx-0 lg:text-left">
              <div className="eyebrow-pill mb-5 inline-flex items-center gap-2 rounded-full border border-accent-blue/25 bg-accent-blue/10 text-[11px] font-semibold uppercase tracking-[0.16em] text-accent-blue">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-blue" />
                Request access
              </div>

              <h2 style={{ color: 'white' }} className="text-3xl font-bold leading-[1.05] tracking-[-0.015em] text-balance sm:text-4xl lg:text-[3.1rem]">
                Start with a short review.
              </h2>

              <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300">
                Share the essentials and we will confirm whether Pipeout is the right fit for your
                outbound workflow.
              </p>

              <div className="mt-6 inline-flex items-center rounded-full border border-white/6 bg-white/[0.03] px-4 py-2 text-sm text-slate-300">
                Human review
                <span className="mx-3 h-1 w-1 rounded-full bg-white/25" />
                Usually within 48 hrs
              </div>
            </div>

            <div className="request-access-form-card rounded-[30px] p-6 sm:p-8">
              <div className="mb-6 text-center lg:text-left">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  Request access
                </p>
                <h3 className="mt-2 text-2xl font-bold text-white">Tell us the essentials</h3>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-400 lg:mx-0">
                  Work email, first name, and company are enough to get started.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="-z-10 absolute opacity-0" aria-hidden="true">
                  <label htmlFor="website_url">Website</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label htmlFor="work_email" className={labelClass}>
                    Work email <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <input
                      type="email"
                      id="work_email"
                      name="work_email"
                      required
                      value={formData.work_email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className={`${inputClass} pl-11`}
                    />
                  </div>
                  {emailError && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-red-300">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {emailError}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="first_name" className={labelClass}>
                      First name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      required
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="Jane"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label htmlFor="company_name" className={labelClass}>
                      Company <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="company_name"
                      name="company_name"
                      required
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder="Acme Inc."
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="border-t border-white/6 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowOptional(!showOptional)}
                    className="flex w-full items-center justify-between rounded-2xl border border-white/6 bg-white/[0.025] px-4 py-3 text-left text-sm font-medium text-slate-200 transition-colors hover:bg-white/[0.04] hover:text-white"
                  >
                    <span>Optional details</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-180 ${
                        showOptional ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showOptional && (
                    <div className="mt-4 rounded-[24px] border border-white/6 bg-black/14 p-4 sm:p-5">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="last_name" className={labelClass}>
                              Last name
                            </label>
                            <input
                              type="text"
                              id="last_name"
                              name="last_name"
                              value={formData.last_name}
                              onChange={handleChange}
                              placeholder="Doe"
                              className={inputClass}
                            />
                          </div>

                          <div>
                            <label htmlFor="role" className={labelClass}>
                              Role
                            </label>
                            <input
                              type="text"
                              id="role"
                              name="role"
                              value={formData.role}
                              onChange={handleChange}
                              placeholder="Head of Sales"
                              className={inputClass}
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="company_website" className={labelClass}>
                            Company website
                          </label>
                          <input
                            type="url"
                            id="company_website"
                            name="company_website"
                            value={formData.company_website}
                            onChange={handleChange}
                            placeholder="https://acme.com"
                            className={inputClass}
                          />
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="monthly_outreach_volume" className={labelClass}>
                              Monthly outreach volume
                            </label>
                            <select
                              id="monthly_outreach_volume"
                              name="monthly_outreach_volume"
                              value={formData.monthly_outreach_volume}
                              onChange={handleChange}
                              className={selectClass}
                            >
                              <option value="" className="bg-[#0c1018] text-white">
                                Select...
                              </option>
                              <option value="<100" className="bg-[#0c1018] text-white">
                                &lt;100
                              </option>
                              <option value="100-500" className="bg-[#0c1018] text-white">
                                100 - 500
                              </option>
                              <option value="500-2000" className="bg-[#0c1018] text-white">
                                500 - 2,000
                              </option>
                              <option value="2000+" className="bg-[#0c1018] text-white">
                                2,000+
                              </option>
                            </select>
                          </div>

                          <div>
                            <label htmlFor="primary_use_case" className={labelClass}>
                              Primary use case
                            </label>
                            <select
                              id="primary_use_case"
                              name="primary_use_case"
                              value={formData.primary_use_case}
                              onChange={handleChange}
                              className={selectClass}
                            >
                              <option value="" className="bg-[#0c1018] text-white">
                                Select...
                              </option>
                              <option value="Lead Generation" className="bg-[#0c1018] text-white">
                                Lead Generation
                              </option>
                              <option
                                value="Account-Based Outreach"
                                className="bg-[#0c1018] text-white"
                              >
                                Account-Based Outreach
                              </option>
                              <option value="Recruiting" className="bg-[#0c1018] text-white">
                                Recruiting
                              </option>
                              <option
                                value="Partnership Outreach"
                                className="bg-[#0c1018] text-white"
                              >
                                Partnership Outreach
                              </option>
                              <option value="Other" className="bg-[#0c1018] text-white">
                                Other
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-[22px] border border-white/6 bg-white/[0.025] p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="consent"
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                      required
                      className="mt-1 h-4 w-4 rounded border-white/30 bg-black/30 text-accent-blue focus:ring-accent-blue/40"
                    />
                    <label htmlFor="consent" className="text-xs leading-relaxed text-slate-400">
                      I agree to the{" "}
                      <a
                        href="/privacy-policy"
                        className="text-accent-blue transition-colors hover:text-white"
                      >
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href="/terms-and-conditions"
                        className="text-accent-blue transition-colors hover:text-white"
                      >
                        Terms &amp; Conditions
                      </a>
                      .
                    </label>
                  </div>
                </div>

                {formState === "error" && (
                  <div className="flex items-start gap-3 rounded-[22px] border border-red-500/25 bg-red-500/10 p-4">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-300" />
                    <div className="text-sm">
                      <p className="font-medium text-red-300">{errorMessage}</p>
                      <p className="mt-1 text-slate-300">
                        You can also reach us at{" "}
                        <a
                          href="mailto:support@pipeout.app"
                          className="text-accent-blue transition-colors hover:text-white"
                        >
                          support@pipeout.app
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={formState === "submitting"}
                    className="btn-primary w-full rounded-[20px] px-6 py-3.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 sm:flex-1"
                  >
                    <span className="flex items-center justify-center gap-2">
                      {formState === "submitting" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        "Request Access"
                      )}
                    </span>
                  </button>

                  <a
                    href="#pipeline"
                    className="btn-secondary w-full rounded-2xl px-5 py-3.5 text-sm font-semibold text-white sm:w-auto"
                  >
                    <span className="flex items-center justify-center gap-2">
                      See How It Works
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
